/**
 * `bluesky` — an author's posts, a custom feed, or a list feed (sources.md "bluesky"), from the public
 * AppView `https://public.api.bsky.app/xrpc/…` (CORS `*`). Handles resolve to a DID once, at creation.
 * Pages with `cursor` until a known post or `maxItems`. Cursor: `{ newestUri, newestAt }`.
 */

import type { BlueskyConfig, MediaItem, RawItem, SourceClient, SourceFetchContext, SourceFetchResult, SourceRuntime } from '../contracts/sources.js';
import { DigesterError } from '../kernel/errors.js';
import { isRecord } from './feed.js';
import { assertOk, escapeHtml, invalidInput, parseDate, parseHttpUrl, parseJson } from './http.js';

export const BSKY_API = 'https://public.api.bsky.app/xrpc';
const PAGE_LIMIT = 50;
/** Pages read per fetch at most (50 posts each); the stop rules usually end it after one. */
const MAX_PAGES = 4;

const DID = /^did:(plc|web):[A-Za-z0-9._:%-]+$/;
const HANDLE = /^@?([a-z0-9][a-z0-9-]*(\.[a-z0-9][a-z0-9-]*)+)$/i;

type Rec = Record<string, unknown>;

async function getJson(runtime: SourceRuntime, url: string, what: string): Promise<unknown> {
  const response = await runtime.fetcher.fetch({ url, method: 'GET', headers: { Accept: 'application/json' }, route: 'auto' });
  assertOk(response, { credential: false, what, now: runtime.clock.now() });
  return parseJson(response.text, what);
}

async function resolveDid(runtime: SourceRuntime, actor: string): Promise<string> {
  if (DID.test(actor)) return actor;
  const handle = actor.replace(/^@/, '').toLowerCase();
  const body = await getJson(runtime, `${BSKY_API}/com.atproto.identity.resolveHandle?handle=${encodeURIComponent(handle)}`, `The handle @${handle}`);
  if (!isRecord(body) || typeof body.did !== 'string') throw new DigesterError('parse_failed', `Could not resolve @${handle}.`);
  return body.did;
}

type Parsed = { mode: BlueskyConfig['mode']; actor: string; rkey?: string };

/** Handle, DID, `https://bsky.app/profile/<actor>[/feed/<rkey>|/lists/<rkey>]`, or an at:// URI. */
export function parseBlueskyAddress(address: string): Parsed | { uri: string; mode: 'feed' | 'list' } | null {
  const raw = address.trim();
  if (raw.startsWith('at://')) {
    if (raw.includes('/app.bsky.feed.generator/')) return { uri: raw, mode: 'feed' };
    if (raw.includes('/app.bsky.graph.list/')) return { uri: raw, mode: 'list' };
    return null;
  }
  if (DID.test(raw)) return { mode: 'author', actor: raw };
  const handle = HANDLE.exec(raw);
  if (handle?.[1]) return { mode: 'author', actor: handle[1].toLowerCase() };
  const url = parseHttpUrl(raw);
  if (url && (url.hostname === 'bsky.app' || url.hostname === 'www.bsky.app')) {
    const parts = url.pathname.split('/').filter((p) => p !== '');
    if (parts[0] !== 'profile' || !parts[1]) return null;
    const actor = decodeURIComponent(parts[1]);
    if (parts[2] === 'feed' && parts[3]) return { mode: 'feed', actor, rkey: parts[3] };
    if (parts[2] === 'lists' && parts[3]) return { mode: 'list', actor, rkey: parts[3] };
    return { mode: 'author', actor };
  }
  return null;
}

// ── rich text ──────────────────────────────────────────────────────────────────────────────────

function utf8Encode(s: string): number[] {
  const out: number[] = [];
  for (const ch of s) {
    const cp = ch.codePointAt(0) as number;
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 63));
    else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    else out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 63), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
  }
  return out;
}

function utf8Decode(bytes: readonly number[]): string {
  let s = '';
  for (let i = 0; i < bytes.length; ) {
    const b = bytes[i] as number;
    let cp: number;
    let n: number;
    if (b < 0x80) [cp, n] = [b, 1];
    else if (b >= 0xf0) [cp, n] = [b & 7, 4];
    else if (b >= 0xe0) [cp, n] = [b & 15, 3];
    else [cp, n] = [b & 31, 2];
    for (let k = 1; k < n; k++) cp = (cp << 6) | ((bytes[i + k] ?? 0) & 63);
    s += String.fromCodePoint(cp);
    i += n;
  }
  return s;
}

const textHtml = (s: string): string => escapeHtml(s).replace(/\n/g, '<br>');

/** Post text + facets (byte ranges: links, mentions, tags) → HTML paragraph. */
export function richTextHtml(text: string, facets: unknown): string {
  const bytes = utf8Encode(text);
  const spans: Array<{ start: number; end: number; href: string }> = [];
  for (const f of Array.isArray(facets) ? facets : []) {
    if (!isRecord(f) || !isRecord(f.index)) continue;
    const start = Number(f.index.byteStart);
    const end = Number(f.index.byteEnd);
    if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end > bytes.length || start >= end) continue;
    const feature = Array.isArray(f.features) ? f.features.find(isRecord) : undefined;
    if (!feature) continue;
    const type = feature.$type;
    let href: string | null = null;
    if (type === 'app.bsky.richtext.facet#link' && typeof feature.uri === 'string') href = feature.uri;
    else if (type === 'app.bsky.richtext.facet#mention' && typeof feature.did === 'string') href = `https://bsky.app/profile/${feature.did}`;
    else if (type === 'app.bsky.richtext.facet#tag' && typeof feature.tag === 'string') href = `https://bsky.app/hashtag/${encodeURIComponent(feature.tag)}`;
    if (href && /^https?:\/\//i.test(href)) spans.push({ start, end, href });
  }
  spans.sort((a, b) => a.start - b.start);
  let html = '';
  let pos = 0;
  for (const s of spans) {
    if (s.start < pos) continue; // overlapping facets: keep the first
    html += textHtml(utf8Decode(bytes.slice(pos, s.start)));
    html += `<a href="${escapeHtml(s.href)}">${textHtml(utf8Decode(bytes.slice(s.start, s.end)))}</a>`;
    pos = s.end;
  }
  html += textHtml(utf8Decode(bytes.slice(pos)));
  return html.trim() === '' ? '' : `<p>${html}</p>`;
}

// ── posts ──────────────────────────────────────────────────────────────────────────────────────

function rkeyOf(uri: string): string {
  return uri.split('/').pop() ?? '';
}

function embedParts(embed: unknown, media: MediaItem[], postUrl: string): string {
  if (!isRecord(embed)) return '';
  const type = typeof embed.$type === 'string' ? embed.$type : '';
  let html = '';
  if (type === 'app.bsky.embed.images#view') {
    for (const img of Array.isArray(embed.images) ? embed.images : []) {
      if (!isRecord(img) || typeof img.fullsize !== 'string') continue;
      const m: MediaItem = { type: 'image', url: img.fullsize };
      if (typeof img.thumb === 'string') m.thumbnailUrl = img.thumb;
      const alt = typeof img.alt === 'string' ? img.alt : '';
      if (alt) m.title = alt;
      media.push(m);
      html += `<figure><img src="${escapeHtml(img.fullsize)}" alt="${escapeHtml(alt)}"></figure>`;
    }
  } else if (type === 'app.bsky.embed.external#view' && isRecord(embed.external)) {
    const ext = embed.external;
    if (typeof ext.uri === 'string' && /^https?:\/\//i.test(ext.uri)) {
      const title = typeof ext.title === 'string' && ext.title ? ext.title : ext.uri;
      const m: MediaItem = { type: 'link', url: ext.uri, title };
      if (typeof ext.thumb === 'string') m.thumbnailUrl = ext.thumb;
      media.push(m);
      html += `<p><a href="${escapeHtml(ext.uri)}">${escapeHtml(title)}</a></p>`;
    }
  } else if (type === 'app.bsky.embed.video#view') {
    const m: MediaItem = { type: 'video', url: postUrl };
    if (typeof embed.thumbnail === 'string') m.thumbnailUrl = embed.thumbnail;
    media.push(m);
  } else if (type === 'app.bsky.embed.record#view') {
    html += quoteHtml(embed.record);
  } else if (type === 'app.bsky.embed.recordWithMedia#view') {
    html += embedParts(embed.media, media, postUrl);
    if (isRecord(embed.record)) html += quoteHtml(embed.record.record);
  }
  return html;
}

function quoteHtml(record: unknown): string {
  if (!isRecord(record) || !isRecord(record.value) || typeof record.value.text !== 'string') return '';
  const author = isRecord(record.author) ? (typeof record.author.displayName === 'string' && record.author.displayName ? record.author.displayName : typeof record.author.handle === 'string' ? record.author.handle : '') : '';
  const body = richTextHtml(record.value.text, record.value.facets);
  const by = author ? `<p><strong>${escapeHtml(author)}</strong></p>` : '';
  return `<blockquote>${by}${body}</blockquote>`;
}

export function postItem(post: Rec): RawItem | null {
  const uri = typeof post.uri === 'string' ? post.uri : null;
  const record = isRecord(post.record) ? post.record : null;
  if (!uri || !record) return null;
  const author = isRecord(post.author) ? post.author : {};
  const handle = typeof author.handle === 'string' ? author.handle : typeof author.did === 'string' ? author.did : '';
  const url = `https://bsky.app/profile/${handle}/post/${rkeyOf(uri)}`;
  const media: MediaItem[] = [];
  const text = typeof record.text === 'string' ? record.text : '';
  const html = richTextHtml(text, record.facets) + embedParts(post.embed, media, url);
  const item: RawItem = { externalId: uri, url };
  if (html) item.html = html;
  else item.text = media.length > 0 ? `[${media[0]?.type === 'image' ? 'Image' : 'Media'}]` : '';
  const name = typeof author.displayName === 'string' && author.displayName.trim() ? author.displayName.trim() : handle;
  if (name) item.author = name;
  const publishedAt = parseDate(typeof record.createdAt === 'string' ? record.createdAt : null);
  if (publishedAt !== undefined) item.publishedAt = publishedAt;
  if (media.length > 0) item.media = media;
  const image = media.find((m) => m.type === 'image') ?? media.find((m) => m.thumbnailUrl);
  if (image) item.imageUrl = image.type === 'image' ? image.url : (image.thumbnailUrl as string);
  if (Array.isArray(record.langs) && typeof record.langs[0] === 'string') item.lang = record.langs[0];
  return item;
}

/** When a feed entry happened: the repost time for a repost, else the post's index time. */
function entryTime(entry: Rec): string | null {
  if (isRecord(entry.reason) && typeof entry.reason.indexedAt === 'string') return entry.reason.indexedAt;
  if (isRecord(entry.post) && typeof entry.post.indexedAt === 'string') return entry.post.indexedAt;
  return null;
}

function feedUrl(c: BlueskyConfig, cursor: string | null): string {
  const page = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
  if (c.mode === 'author') {
    const filter = c.includeReplies ? 'posts_with_replies' : 'posts_no_replies';
    return `${BSKY_API}/app.bsky.feed.getAuthorFeed?actor=${encodeURIComponent(c.actor ?? '')}&limit=${PAGE_LIMIT}&filter=${filter}${page}`;
  }
  if (c.mode === 'feed') return `${BSKY_API}/app.bsky.feed.getFeed?feed=${encodeURIComponent(c.uri ?? '')}&limit=${PAGE_LIMIT}${page}`;
  return `${BSKY_API}/app.bsky.feed.getListFeed?list=${encodeURIComponent(c.uri ?? '')}&limit=${PAGE_LIMIT}${page}`;
}

export const blueskyClient: SourceClient<'bluesky'> = {
  kind: 'bluesky',
  defaultPullMinutes: 15,

  async resolve(input, runtime) {
    const parsed = parseBlueskyAddress(input.address);
    if (!parsed) throw invalidInput('Enter a Bluesky handle, a DID, a profile / feed / list URL, or an at:// URI.');
    const base = { kind: 'bluesky' as const, includeReplies: input.config?.includeReplies ?? false, includeReposts: input.config?.includeReposts ?? false };
    if ('uri' in parsed) {
      return { kind: 'bluesky', locator: `bluesky:${parsed.mode}:${parsed.uri}`, name: `Bluesky ${parsed.mode} ${rkeyOf(parsed.uri)}`, config: { ...base, mode: parsed.mode, uri: parsed.uri }, requiredSecrets: [] };
    }
    const did = await resolveDid(runtime, parsed.actor);
    if (parsed.mode === 'author') {
      const name = DID.test(parsed.actor) ? did : `@${parsed.actor.replace(/^@/, '')}`;
      return { kind: 'bluesky', locator: `bluesky:author:${did}`, name, config: { ...base, mode: 'author', actor: did }, requiredSecrets: [] };
    }
    const collection = parsed.mode === 'feed' ? 'app.bsky.feed.generator' : 'app.bsky.graph.list';
    const uri = `at://${did}/${collection}/${parsed.rkey ?? ''}`;
    return { kind: 'bluesky', locator: `bluesky:${parsed.mode}:${uri}`, name: `Bluesky ${parsed.mode} ${parsed.rkey ?? ''}`, config: { ...base, mode: parsed.mode, uri }, requiredSecrets: [] };
  },

  async fetch(ctx: SourceFetchContext<'bluesky'>): Promise<SourceFetchResult> {
    const c = ctx.source.config;
    const knownUri = typeof ctx.cursor?.newestUri === 'string' ? ctx.cursor.newestUri : null;
    const knownAt = parseDate(typeof ctx.cursor?.newestAt === 'string' ? ctx.cursor.newestAt : null);
    const items: RawItem[] = [];
    let newestUri = knownUri;
    let newestAtText = typeof ctx.cursor?.newestAt === 'string' ? ctx.cursor.newestAt : null;
    let page: string | null = null;
    let reachedKnown = false;
    for (let n = 0; n < MAX_PAGES && !reachedKnown && items.length < ctx.maxItems; n++) {
      const body = await getJson(ctx.runtime, feedUrl(c, page), ctx.source.name || 'Bluesky');
      if (!isRecord(body) || !Array.isArray(body.feed)) throw new DigesterError('parse_failed', 'The Bluesky feed has no "feed" list.');
      for (const entry of body.feed.filter(isRecord)) {
        const post = isRecord(entry.post) ? entry.post : null;
        if (!post || typeof post.uri !== 'string') continue;
        const reasonType = isRecord(entry.reason) && typeof entry.reason.$type === 'string' ? entry.reason.$type : null;
        const pinned = reasonType === 'app.bsky.feed.defs#reasonPin';
        const time = entryTime(entry);
        if (!pinned) {
          // Stop at the first post this cursor already covers.
          if (post.uri === knownUri || (knownAt !== undefined && time !== null && (parseDate(time) ?? 0) <= knownAt)) {
            reachedKnown = true;
            break;
          }
          if (newestAtText === null || (time !== null && (parseDate(time) ?? 0) > (parseDate(newestAtText) ?? 0))) {
            newestAtText = time;
            newestUri = post.uri;
          }
        }
        if (reasonType === 'app.bsky.feed.defs#reasonRepost' && !c.includeReposts) continue;
        const record = isRecord(post.record) ? post.record : {};
        if (!c.includeReplies && isRecord(record.reply)) continue;
        const item = postItem(post);
        if (item && (item.html || item.text)) items.push(item);
        if (items.length >= ctx.maxItems) break;
      }
      page = typeof body.cursor === 'string' && body.cursor ? body.cursor : null;
      if (!page || knownUri === null) break; // the first fetch reads one page
    }
    const cursor = { ...(ctx.cursor ?? {}), ...(newestUri ? { newestUri } : {}), ...(newestAtText ? { newestAt: newestAtText } : {}) };
    return { items: items.slice(0, ctx.maxItems), cursor };
  },
};
